import { MaterialCommunityIcons } from '@expo/vector-icons';
import { IssueType } from '@gripp/shared-logic';
import {
  Colors,
  Icon,
  IssueHeaderProps,
  severityToVariant,
  SlideAction,
  TypeDropdown,
  TypeVariant,
  useSlideLayout,
  variantToSeverity,
} from '@gripp/shared-ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { IconButton, Text } from 'react-native-paper';
import { useNavigate } from 'react-router-dom';

export const IssueHeaderCropCircle: React.FC<IssueHeaderProps> = (
  props: IssueHeaderProps
) => {
  const { onSlideChange, column } = useSlideLayout();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const issue = props.issue;

  const [currentSeverity, setSeverity] = useState(issue.severity);

  useEffect(() => {
    setSeverity(issue.severity);
  }, [issue]);

  const handleSeverityChange = (variant: TypeVariant) => {
    const severity = variantToSeverity[variant];
    if (severity) {
      setSeverity(severity);
      props.onChanged(severity);
    }
  };

  const isSeverityDisabled = () => {
    return (
      !issue ||
      issue.type === IssueType.Feedback ||
      issue.type === IssueType.Routine
    );
  };

  const handleBackPress = async () => {
    if (props.onBackPress) {
      props.onBackPress();
    } else {
      await onSlideChange(SlideAction.SINGLE);
      navigate(-1);
    }
  };

  const handleAssetPress = async () => {
    if (props.onAssetPress) {
      props.onAssetPress();
    }
  };

  const onEditPress = async () => {
    await onSlideChange(SlideAction.NEXT);

    navigate(`/activities/${props.issue.id}/edit`);
  };

  if (!issue) {
    return;
  }

  return (
    <View style={styles.headerContainer}>
      {/* Nav bar */}
      <View style={styles.headerBar}>
        <Pressable style={styles.headerButton} onPress={handleBackPress}>
          <Icon name="Arrow Left" size={20} color={Colors.black} />
          <Text style={styles.headerButtonText} numberOfLines={1}>
            {t('activity.menu.titlePlural')}
          </Text>
        </Pressable>

        {issue.asset && props.onAssetPress && column !== 'left' && (
          <Pressable
            style={[styles.headerButton, styles.headerButtonRight]}
            onPress={handleAssetPress}
          >
            <Text style={styles.headerButtonText}>
              {t('assets.menu.titleSingular')}
            </Text>
            <Icon name="Chevron Right" size={20} color={Colors.black} />
          </Pressable>
        )}
      </View>

      {/* Card */}
      <View style={styles.header}>
        <View style={styles.mainContainerHeader}>
          {/* Left: TypeDropdown */}
          <View style={styles.leftSection}>
            <TypeDropdown
              value={severityToVariant[currentSeverity]}
              onChange={handleSeverityChange}
              disabled={isSeverityDisabled()}
              showLabel={false}
              testID="issue-detail-severity-dropdown"
              availableTypes={['urgent', 'pending', 'solved']}
            />
          </View>

          {/* Middle: Title and Subtitle */}
          <View style={styles.middleSection}>
            <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
              {issue.title}
            </Text>
            <Text
              style={styles.subtitle}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {issue.asset?.name || issue.workspace?.name}
            </Text>
          </View>

          {/* Right: Menu */}
          <View style={styles.rightSection}>
            {!props.hideMenu && (
              <View style={styles.menuContainer}>
                {issue.isPinned && (
                  <MaterialCommunityIcons
                    name="pin"
                    color={Colors.black}
                    size={20}
                    style={styles.pinnedIcon}
                  />
                )}
                {props.onPrint && (
                  <IconButton
                    icon="printer"
                    size={20}
                    iconColor={Colors.black}
                    onPress={props.onPrint}
                    style={styles.printButton}
                  />
                )}
                <Pressable onPress={onEditPress}>
                  <Icon name="Settings" size={20} color={Colors.black} />
                </Pressable>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
const styles = StyleSheet.create({
  headerContainer: {
    backgroundColor: Colors.primaryGrayBackground,
    zIndex: 2,
  },
  headerBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 65,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: Colors.white,
    borderRadius: 4,
    marginBottom: 2,
  },
  headerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    overflow: 'hidden',
  },
  headerButtonRight: {
    flexShrink: 0,
  },
  headerButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.black,
    flexShrink: 1,
  },
  header: {
    backgroundColor: Colors.white,
    paddingTop: 16,
  },
  mainContainerHeader: {
    borderRadius: 4,
    backgroundColor: Colors.slate[100],
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    padding: 16,
    gap: 8,
  },
  leftSection: {
    flexShrink: 0,
  },
  middleSection: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.black,
  },
  subtitle: {
    fontSize: 13,
    fontWeight: '400',
    color: Colors.black,
    opacity: 0.6,
  },
  rightSection: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  pinnedIcon: {
    width: 20,
    height: 20,
    backfaceVisibility: 'hidden',
  },
  printButton: {
    margin: 0,
    width: 20,
    height: 20,
  },
});

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      count
      items {
        id
        name
        workspace {
          id
        }
        trigger {
          type
          config
        }
        action {
          type
          config
        }
      }
    }
  }
`;
